import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import Image1 from "../../images/exams/image1.png";
import SnackbarAlert from "../../components/SnackBar";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { buildExamFormData } from "../../helper/ExamFormData";
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";

function UpdateExam() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [course, setCourse] = useState(null);
    const [exam, setExam] = useState(null);
    const [nameEn, setNameEn] = useState("");
    const [nameAr, setNameAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [durationMinutes, setDurationMinutes] = useState("30");
    const [passPercentage, setPassPercentage] = useState("50");
    const [maxAttempts, setMaxAttempts] = useState("3");
    const param = useParams();
    const navigate = useNavigate();

    const courseDetailsPath = `/course-details/${param.course_id}`;

    const courseName = useMemo(() => {
        if (!course) return "";
        return isAr ? course.name_ar : course.name_en;
    }, [course, isAr]);

    const examName = useMemo(() => {
        if (!exam) return "";
        return isAr ? exam.name_ar : exam.name_en;
    }, [exam, isAr]);

    const courseImage = resolveCourseImage(course);
    const questionCount = exam?.questions?.length ?? 0;

    const fillFormFromExam = (data) => {
        setNameEn(data.name_en || "");
        setNameAr(data.name_ar || "");
        setDescriptionEn(data.description_en || "");
        setDescriptionAr(data.description_ar || "");
        setDurationMinutes(String(data.duration_minutes ?? 30));
        setPassPercentage(String(data.pass_percentage ?? 50));
        setMaxAttempts(String(data.max_attempts ?? 3));
    };

    const loadData = async () => {
        setGetWait(true);

        const [courseResult, examResult] = await Promise.all([
            Fetch(`${host}/courses/${param.course_id}/show`, "GET", null),
            Fetch(`${host}/courses/${param.course_id}/exams/${param.exam_id}/show`, "GET", null),
        ]);

        if (courseResult.status === 200) {
            setCourse(courseResult.data.data);
        }

        if (examResult.status === 200) {
            const examData = examResult.data.data;
            setExam(examData);
            fillFormFromExam(examData);
        }

        setGetWait(false);
    };

    useEffect(() => {
        loadData();
    }, [param.course_id, param.exam_id]);

    const updateExam = async () => {
        if (!nameEn.trim() || !nameAr.trim()) {
            setSnackBar("error", isAr ? "أدخل اسم الاختبار بالعربية والإنجليزية" : "Enter exam name in Arabic and English");
            return;
        }
        if (!descriptionEn.trim() || !descriptionAr.trim()) {
            setSnackBar("error", isAr ? "أدخل وصف الاختبار بالعربية والإنجليزية" : "Enter exam description in Arabic and English");
            return;
        }

        setSendWait(true);

        const formData = buildExamFormData({
            nameEn: nameEn.trim(),
            nameAr: nameAr.trim(),
            descriptionEn: descriptionEn.trim(),
            descriptionAr: descriptionAr.trim(),
            courseId: param.course_id,
            durationMinutes,
            passPercentage,
            maxAttempts,
        });

        const result = await Fetch(`${host}/teacher/courses/exams/${param.exam_id}/update`, "POST", formData);

        if (result.status === 200) {
            setSnackBar("success", isAr ? "تم تحديث الاختبار بنجاح" : "Exam updated successfully");
            setExam(result.data.data);
            fillFormFromExam(result.data.data);
        } else if (result.status === 422) {
            setSnackBar("error", result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar("error", isAr ? "فشل تحديث الاختبار" : "Failed to update exam");
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "تعديل الاختبار" : "Update exam"}
                    subtitle={isAr ? "تحديث بيانات الاختبار وإعداداته" : "Update exam details and settings"}
                    icon={<AssignmentIcon />}
                    actions={
                        <>
                            <Button
                                variant="contained"
                                startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                                onClick={updateExam}
                                disabled={sendWait}
                                sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                            >
                                {isAr ? "حفظ التعديلات" : "Save changes"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(courseDetailsPath)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للدورة" : "Back to course"}
                            </Button>
                        </>
                    }
                />

                {course || exam ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            {course ? (
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        flexShrink: 0,
                                        bgcolor: "#0D358C",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {courseImage ? (
                                        <Box component="img" src={courseImage} alt={courseName} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                        <MenuBookOutlinedIcon sx={{ color: "white", fontSize: 36 }} />
                                    )}
                                </Box>
                            ) : null}

                            <Box sx={{ flex: 1, minWidth: 220 }}>
                                {course ? (
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {isAr ? "الدورة" : "Course"}: {courseName}
                                    </Typography>
                                ) : null}
                                {exam ? (
                                    <>
                                        <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C", mt: course ? 0.5 : 0 }} noWrap>
                                            {examName}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                            <Chip size="small" label={`ID #${exam.id}`} />
                                            <Chip
                                                size="small"
                                                icon={<QuizOutlinedIcon />}
                                                label={`${isAr ? "الأسئلة" : "Questions"}: ${questionCount}`}
                                                sx={{ bgcolor: "#eff6ff", color: "#1d4ed8" }}
                                            />
                                        </Box>
                                    </>
                                ) : null}
                            </Box>
                        </Box>
                    </ScrollReveal>
                ) : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        direction: "ltr",
                        gap: 3,
                        alignItems: "stretch",
                    }}
                >
                    <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                        <Box
                            sx={{
                                height: "100%",
                                minHeight: { lg: 420 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                p: 4,
                                borderRadius: 4,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                background: theme.palette.mode === "dark"
                                    ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                    : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                            }}
                        >
                            <Box
                                component="img"
                                src={Image1}
                                alt=""
                                sx={{ width: "100%", maxWidth: 300, height: "auto", objectFit: "contain" }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 280, lineHeight: 1.7 }}>
                                {isAr
                                    ? "يمكنك تعديل اسم الاختبار ووصفه وإعدادات المدة ونسبة النجاح في أي وقت"
                                    : "You can update the exam name, description, duration, and pass settings at any time"}
                            </Typography>
                        </Box>
                    </ScrollReveal>

                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0, direction: isAr ? "rtl" : "ltr" }}>
                        <ScrollReveal delay={100}>
                            <DashboardPanelCard
                                title={isAr ? "معلومات الاختبار" : "Exam information"}
                                subtitle={isAr ? "الاسم والوصف بالعربية والإنجليزية" : "Name and description in Arabic and English"}
                            >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                        <TextField
                                            sx={fieldSx}
                                            value={nameEn}
                                            onChange={(e) => setNameEn(e.target.value)}
                                            label={isAr ? "اسم الاختبار بالإنجليزية" : "Exam name (English)"}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                        <TextField
                                            sx={fieldSx}
                                            value={nameAr}
                                            onChange={(e) => setNameAr(e.target.value)}
                                            label={isAr ? "اسم الاختبار بالعربية" : "Exam name (Arabic)"}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Box>
                                    <TextField
                                        sx={fieldSx}
                                        value={descriptionEn}
                                        onChange={(e) => setDescriptionEn(e.target.value)}
                                        label={isAr ? "وصف الاختبار بالإنجليزية" : "Exam description (English)"}
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        required
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        value={descriptionAr}
                                        onChange={(e) => setDescriptionAr(e.target.value)}
                                        label={isAr ? "وصف الاختبار بالعربية" : "Exam description (Arabic)"}
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        required
                                    />
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>

                        <ScrollReveal delay={150}>
                            <DashboardPanelCard
                                title={isAr ? "إعدادات الاختبار" : "Exam settings"}
                                subtitle={isAr ? "مدة الاختبار ونسبة النجاح وعدد المحاولات" : "Duration, pass score, and attempt limits"}
                            >
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={durationMinutes}
                                        onChange={(e) => setDurationMinutes(e.target.value)}
                                        label={isAr ? "المدة (دقيقة)" : "Duration (minutes)"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TimerOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 1, max: 600 }}
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={passPercentage}
                                        onChange={(e) => setPassPercentage(e.target.value)}
                                        label={isAr ? "نسبة النجاح %" : "Pass %"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PercentOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 0, max: 100 }}
                                    />
                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={maxAttempts}
                                        onChange={(e) => setMaxAttempts(e.target.value)}
                                        label={isAr ? "عدد المحاولات (0 = غير محدود)" : "Max attempts (0 = unlimited)"}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <ReplayOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 0 }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                        justifyContent: "flex-end",
                                        mt: 3,
                                        pt: 2,
                                        borderTop: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate(courseDetailsPath)}
                                        disabled={sendWait}
                                    >
                                        {isAr ? "إلغاء" : "Cancel"}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={updateExam}
                                        disabled={sendWait}
                                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                                        sx={{
                                            minWidth: 200,
                                            px: 3,
                                            bgcolor: "#0D358C",
                                            fontWeight: 700,
                                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                                            "&:hover": { bgcolor: "#1d4ed8" },
                                        }}
                                    >
                                        {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "حفظ التعديلات" : "Save changes")}
                                    </Button>
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Box>
                </Box>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default UpdateExam;
